<template>
  <div>
    <v-form ref="form" v-model="valid" class="campaign-shopper-account-form">
      <!--
        PR C1 of the Store Editor Redesign closing chain. The Store Login
        section is two cards: "Store URL" (slug builder + copy + QR) and
        "Who can shop here" (4-mode radio group + the matching config
        block). The radio group replaces the pre-redesign pair of
        independent toggles ('Require a password' + 'Restrict email
        domain') with a single source of truth: ``campaign.access_mode``.
      -->

      <!-- ============================================================
           Card 1: Store URL
           ============================================================ -->
      <editor-card
        title="Store URL"
        description="The link shoppers visit. Make it short and recognizable."
      >
        <v-row>
          <v-col cols="12" sm="8" md="8">
            <v-form v-model="customUrlValid">
              <v-text-field
                v-model="customLink"
                :rules="[validateURL]"
                :error-messages="customLinkError ? [customLinkError] : []"
                @update:modelValue="onCustomLinkInput"
                :prefix="shopPrefix"
                label="Custom store link"
                data-testid="store-login-custom-link"
                placeholder="your-store-name"
                persistent-placeholder
                hint="Must start with a letter. Lowercase letters, numbers, and hyphens. Must be unique across ThreadFlow."
                persistent-hint
              />
            </v-form>
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <v-btn
              variant="flat"
              @click="copyUrlToClipboard"
              :disabled="!customUrlValid"
              class="ml-0 copy-url-button"
            >
              <v-icon class="mr-1" icon="content_copy" />
              Copy link
            </v-btn>
            <v-btn
              variant="flat"
              @click="downloadQRCode"
              :disabled="!customUrlValid"
              class="ml-0 download-qr-button"
            >
              <v-icon class="mr-1" icon="qr_code_2" />
              Download QR Code
            </v-btn>
            <div id="qrcode" hidden></div>
            <a id="qrdl" hidden>Download</a>
          </v-col>
        </v-row>
      </editor-card>

      <!-- ============================================================
           Card 2: Who can shop here  (4-mode radio + config block)
           ============================================================ -->
      <editor-card
        title="Who can shop here"
        description="Choose one access mode. Configure it below."
      >
        <v-row>
          <v-col cols="12">
            <v-radio-group
              v-model="accessMode"
              data-testid="access-mode-radio-group"
              @update:modelValue="$emit('campaign-changed')"
            >
              <div class="access-mode-option">
                <v-radio value="open" data-testid="access-mode-open">
                  <template #label>
                    <span data-testid="access-mode-open-label">
                      Anyone with the link
                    </span>
                  </template>
                </v-radio>
                <div class="access-mode-help">
                  No restrictions. Anyone who finds the URL can shop. Best
                  for marketing campaigns where you want zero friction.
                </div>
              </div>

              <div class="access-mode-option">
                <v-radio
                  value="email_domain"
                  data-testid="access-mode-email-domain"
                >
                  <template #label>
                    <span data-testid="access-mode-email-domain-label">
                      Email-domain restricted
                    </span>
                  </template>
                </v-radio>
                <div class="access-mode-help">
                  Shoppers must sign in with an email address on an approved
                  domain. Best for employee stores.
                </div>
              </div>

              <div class="access-mode-option">
                <v-radio
                  value="password"
                  data-testid="access-mode-password"
                  :disabled="!customLink || !customUrlValid"
                >
                  <!-- The radio root spans the row, so a click on its
                       centre lands on empty space; scenarios click the
                       label, which selects the radio like a user does. -->
                  <template #label>
                    <span data-testid="access-mode-password-label">
                      Shared password
                    </span>
                  </template>
                </v-radio>
                <div class="access-mode-help">
                  Anyone who knows the password can sign in. Simple, but less
                  secure than email-domain. Good for short campaigns or
                  trade-show booths.
                </div>
              </div>

              <div class="access-mode-option">
                <div class="d-flex align-center">
                  <v-radio
                    label="Invite codes"
                    value="invite_codes"
                    :disabled="true"
                    data-testid="access-mode-invite-codes"
                  />
                  <span
                    class="ml-2 access-mode-coming-soon"
                    data-testid="access-mode-invite-codes-coming-soon"
                  >
                    Coming soon
                  </span>
                </div>
                <div class="access-mode-help">
                  Generate single-use codes and distribute them. Each shopper
                  redeems one. Best when the shopper list is fixed and small.
                </div>
              </div>
            </v-radio-group>
          </v-col>

          <!--
            The choice on screen cannot be saved yet (Email-domain restricted
            with no domain, Shared password with no password): it is kept as
            an unsaved draft, the store keeps its saved mode, and this says
            so. Nothing invalid is autosaved (Bug Intake #313, #343, #346).
          -->
          <v-col v-if="pendingAccessMessage" cols="12">
            <v-alert
              type="info"
              variant="tonal"
              density="comfortable"
              data-testid="store-login-unsaved-mode"
            >
              {{ pendingAccessMessage }}
            </v-alert>
          </v-col>

          <!--
            The payroll + open dead end. This store bills shoppers later
            instead of taking a card, and the backend will not extend that
            credit on a store anyone with the link can shop. Left as-is, a
            shopper reaches checkout and cannot complete an order by any
            means available to them -- so the warning names the OTHER half
            of the conflict (the payment method, which lives on a different
            page) rather than just flagging this one.
          -->
          <v-col v-if="hasPayrollAccessConflict" cols="12">
            <v-alert
              type="warning"
              variant="tonal"
              density="comfortable"
              data-testid="payroll-access-conflict"
            >
              No one can check out of this store. Its payment method is
              "Invoice / Payroll Deductions", which bills shoppers later
              rather than taking a card, so the store has to be restricted
              to a known group. Choose email-domain or password access
              above, or change the payment method to credit card on the
              Store Payment Options page.
            </v-alert>
          </v-col>

          <!--
            Net Checkout uses the store password (P1-1): the server refuses
            leaving Shared password while that is so (rule
            net_password_credential_needs_password_mode). Said up front, from
            the SAVED state, so the owner knows where to change it.
          -->
          <v-col v-if="netUsesStorePassword" cols="12">
            <v-alert
              type="info"
              variant="tonal"
              density="comfortable"
              data-testid="store-login-net-password-note"
            >
              Net Checkout on this store uses the store password, so "Who can
              shop here" must stay "Shared password". To change it, first
              switch Net Checkout to a PIN (or turn it off) under Payment
              options. Changing the store password signs out every shopper
              who entered the old one, including for Net Checkout.
            </v-alert>
          </v-col>

          <!--
            Why the server refused the last save of this section (a store
            password that is too short, the "[PASSWORD SAVED]" placeholder,
            a login clash). Set by the editor's save step via showSaveError;
            autosave raises no toast, so this is where the owner sees it.
          -->
          <v-col v-if="saveError" cols="12">
            <v-alert
              type="error"
              variant="tonal"
              density="comfortable"
              data-testid="store-login-save-error"
            >
              {{ saveError }}
            </v-alert>
          </v-col>

          <!-- Per-mode config block: renders only for the selected mode. -->

          <!-- Email-domain mode: the existing StoreAccessControl child. -->
          <v-col v-if="accessMode === 'email_domain'" cols="12">
            <campaign-access-control
              ref="accessControl"
              :campaign="campaign"
              :edit-mode="editMode"
              @domains-changed="screenDomains = $event"
              @campaign-changed="$emit('campaign-changed')"
            />
          </v-col>

          <!-- Password mode: password input + sign-in preview card. -->
          <template v-if="accessMode === 'password'">
            <v-col cols="12" sm="12" md="12">
              <div class="md-subheader-no-spacing">Store password</div>
              <div class="md-body-no-spacing access-mode-help ml-0">
                Share this with shoppers out-of-band (email blast, internal
                portal). Anyone who has it can sign in.
              </div>
              <!--
                Whether a password is SET, in words (Bug Intake #394). The
                saved password is never put in the field (no stand-in value
                either): the field is only for typing a new one. Not shown
                until the saved state has been read, so it never claims "not
                set" for a store that has a password. The saved password can
                be revealed below on request (#496); it is fetched only when
                the eye is clicked and never written into the field.
              -->
              <div
                v-if="savedAccess"
                class="md-body-no-spacing ml-0 mt-2"
                data-testid="store-login-password-status"
              >
                <strong
                  v-if="hasStorePassword"
                  data-testid="store-login-password-set"
                  >A store password is set.</strong
                >
                <strong v-else data-testid="store-login-password-unset"
                  >No store password is set yet.</strong
                >
                {{ passwordStatusDetail }}
              </div>
              <!--
                The SAVED password (Bug Intake #496). Revealable when the
                server holds a sealed copy (store_password_revealable); fetched
                from POST /stores/<id>/store_password/reveal/ only when the eye
                is clicked, held in memory only while shown, and never put in
                the new-password field (it is not something to save). A
                password with no sealed copy (saved before #496, changed elsewhere, or
                a duplicated store) cannot be shown.
              -->
              <div
                v-if="savedAccess && hasStorePassword"
                class="md-body-no-spacing ml-0 mt-2 d-flex align-center"
                data-testid="store-login-saved-password"
              >
                <template v-if="savedPasswordRevealable">
                  <span class="mr-2">Current password:</span>
                  <code
                    v-if="revealedPassword !== null"
                    data-testid="store-login-saved-password-value"
                    >{{ revealedPassword }}</code
                  >
                  <span v-else aria-hidden="true">&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;</span>
                  <v-icon
                    class="ml-2"
                    :icon="revealedPassword !== null ? 'visibility_off' : 'visibility'"
                    :aria-label="
                      revealedPassword !== null
                        ? 'Hide the current password'
                        : 'Show the current password'
                    "
                    role="button"
                    :disabled="revealingPassword"
                    data-testid="store-login-saved-password-reveal"
                    @click="toggleSavedPassword"
                  />
                </template>
                <span v-else data-testid="store-login-password-not-revealable">
                  {{ notRevealableMessage }}
                </span>
              </div>
              <div
                v-if="savedPasswordError"
                class="md-body-no-spacing access-mode-help ml-0"
                data-testid="store-login-saved-password-error"
              >
                {{ savedPasswordError }}
              </div>
              <div
                v-if="savedAccess && savedAccess.store_password_storage_available === false"
                class="md-body-no-spacing access-mode-help ml-0"
                data-testid="store-login-password-storage-unavailable"
              >
                {{ storageUnavailableMessage }}
              </div>
              <v-text-field
                autocomplete="new-password"
                :type="showPassword ? 'text' : 'password'"
                :rules="passwordRules"
                :label="passwordLabel"
                :placeholder="passwordPlaceholder"
                persistent-placeholder
                v-model="password"
                class="input-group--focused"
                @update:modelValue="changePassword()"
                data-testid="ct-client-password"
              >
                <template #append-inner>
                  <!-- Only with something typed: it reveals what the owner
                       typed (#394). The SAVED password has its own eye
                       above (#496). -->
                  <v-icon
                    v-if="password"
                    :icon="showPassword ? 'visibility_off' : 'visibility'"
                    :aria-label="showPassword ? 'Hide password' : 'Show password'"
                    role="button"
                    data-testid="store-login-password-visibility"
                    @click="showPassword = !showPassword"
                  />
                </template>
              </v-text-field>
              <div
                v-if="passwordTooShort && hasStorePassword"
                class="md-body-no-spacing access-mode-help ml-0"
                data-testid="store-login-password-too-short"
              >
                {{ passwordTooShortHint }} The current password is kept until
                then.
              </div>
              <div
                class="md-body-no-spacing access-mode-help ml-0"
                data-testid="store-login-password-rule"
              >
                A Shared password store always needs a password, so it cannot
                be left blank. To stop asking shoppers for one, choose a
                different sign-in option above. The password is kept, so
                switching back to Shared password uses it again.
              </div>
            </v-col>
            <v-col cols="12" sm="12" md="12">
              <div class="md-subheader-no-spacing">
                Create your sign in screen
              </div>
              <div class="md-body-no-spacing">
                Upload a custom logo to represent your store. Recommended
                dimensions 1x1. Format JPG or PNG. The login button will
                automatically match your storefront banner color.
              </div>
            </v-col>
            <!--
              The logo control is the shared ImageUploader (the same one
              Branding uses), not a click target hidden behind the preview
              image: that bespoke Dropzone showed a placeholder that 404'd, so
              the control rendered as an invisible blank square (#4282).
            -->
            <v-col cols="12" md="6">
              <image-uploader
                testid-prefix="store-login-logo"
                :current-image="signInLogoUrl"
                :uploading="imageUploading"
                title="Add a sign-in logo"
                hint="JPG or PNG, square works best."
                @upload="onSignInLogoUpload"
                @remove="onSignInLogoRemove"
                @error="onSignInLogoError"
              />
            </v-col>
            <v-col cols="12">
              <editor-card
                title="Sign-in screen preview"
                description="Preview of the storefront sign-in screen shoppers will see."
              >
                <div data-testid="ShopperCard" class="password-login-card">
                  <div class="d-flex flex-column align-center justify-center">
                    <!-- A plain <img>, not v-img: v-img defers its <img> until
                         scrolled into view, and a preview should not. -->
                    <img
                      :class="[
                        'company-logo mb-4',
                        { 'company-logo--fit': !signInLogoUrl },
                      ]"
                      :src="signInPreviewSrc"
                      alt="Sign-in logo preview"
                      data-testid="pu-po-image"
                    />
                    <div style="width: 350px">
                      <v-text-field
                        id="newPassword"
                        prepend-inner-icon="lock"
                        name="password"
                        type="password"
                        value="*******************"
                        data-testid="shopper-password"
                        readonly
                        variant="solo"
                        rounded
                      />
                      <v-btn
                        block
                        :color="loginButtonColor"
                        data-testid="submit"
                        :class="['btn-login', `text-${loginButtonTextColor}`]"
                        rounded="pill"
                        >Start Shopping
                      </v-btn>
                    </div>
                  </div>
                </div>
              </editor-card>
            </v-col>
          </template>
        </v-row>
      </editor-card>
    </v-form>
  </div>
</template>
<script>
import { describeError } from "@/utils/safeErrorLog";
import helpers from "@/mixins/helpers";
import rules from "@/mixins/rules";
import Account from "@/models/Account";
import TabError from "./TabError.vue";
import CampaignAccessControl from "./StoreAccessControl.vue";
import EditorCard from "./components/EditorCard.vue";
import ImageUploader from "@/components/ImageUploader.vue";
import { isUnsellablePaymentConfiguration } from "@/utils/campaign";
import {
  PROBLEMS,
  accessConfigProblems,
  MIN_STORE_PASSWORD_LENGTH,
  PASSWORD_TOO_SHORT_MESSAGE,
} from "@/utils/storeAccessRules";
import { ROLE_SLUGS } from "./storeAccountRoles";
import { STORAGE_UNAVAILABLE_MESSAGE } from "./shopperSaveRefusal";
import {
  DEFAULT_SIGN_IN_LOGO_SRC,
  SIGN_IN_NO_LOGO_SENTINEL,
} from "@/utils/signInLogo";

// The value the editor used to put in the field for a stored password. It
// is no longer shown (#394: "set" is said in words), but the server still
// refuses it as a password (password_policy.SAVED_PASSWORD_PLACEHOLDER), so
// it is never sent.
const SAVED_PASSWORD_PLACEHOLDER = "[PASSWORD SAVED]";

// Revealing the SAVED password (#496): the editor's own words for each
// refusal code of POST /stores/<id>/store_password/reveal/. Server text is
// never shown.
const REVEAL_FAILED_MESSAGE =
  "The current password could not be shown. Please try again.";
const REVEAL_MESSAGES = {
  store_password_not_revealable:
    "The saved password can't be shown. Set a new one to enable reveal.",
  store_password_storage_unavailable: STORAGE_UNAVAILABLE_MESSAGE,
};

const MODE_LABELS = {
  open: "Anyone with the link",
  email_domain: "Email-domain restricted",
  password: "Shared password",
  invite_codes: "Invite codes",
};

// PR C1: the four valid access modes. Keep in sync with the
// backend allowlist (api/src/models/store.py:update_campaign) and the
// migration (route_fork_schema_030).
const VALID_ACCESS_MODES = ["open", "email_domain", "password", "invite_codes"];

// The mode a store object holds: ``access_mode`` when valid, else the
// legacy flags.
function modeOf(campaign) {
  const raw = campaign?.access_mode;
  if (VALID_ACCESS_MODES.includes(raw)) return raw;
  return deriveModeFromLegacy(campaign);
}

function deriveModeFromLegacy(campaign) {
  // Backfill rule used by the migration -- mirrored here for the
  // editor's read path when ``campaign.access_mode`` is absent (e.g.
  // legacy clients or pre-migration cached objects).
  if (!campaign) return "open";
  if (campaign.storefront_password) return "password";
  if (campaign.is_restrict_email_domain) return "email_domain";
  return "open";
}

export default {
  components: {
    TabError,
    CampaignAccessControl,
    EditorCard,
    ImageUploader,
  },

  props: {
    campaign: Object,
    editMode: Boolean,
    targetRole: Object,
    otherRole: Object,
    reportsDisabled: Boolean,
    loginField: {
      type: String,
      default: "Login *",
    },
    passwordField: {
      type: String,
      default: "Password *",
    },
    usernameError: String,
    emailError: String,
    // The store's SAVED Store Login state, read from the server after load
    // and after every save (GET /stores/<id>/access_state/). Carries
    // ``has_store_password``, which the store row alone cannot tell (#342).
    savedAccess: { type: Object, default: null },
  },

  mixins: [rules, helpers],

  computed: {
    baseUrl() {
      return process.env.BASE_URL || window.location.origin;
    },

    /**
     * The text-field prefix shown in front of the slug input
     * (e.g. ``https://shop.threadflow.app/shop/``). Computed rather
     * than bound inline so an empty ``baseUrl`` can never collapse to
     * the invalid ``//shop/`` (which Vue rejects when it falls through
     * as a DOM attribute, producing a "Failed setting prop prefix"
     * warning). Trailing slashes on the base are stripped so we never
     * emit a double slash.
     */
    shopPrefix() {
      const base = (this.baseUrl || "").replace(/\/+$/, "");
      return `${base}/shop/`;
    },

    /**
     * Payroll tender on an ungated store -- a configuration no shopper can
     * ever check out of. Delegated to the shared helper so this page and
     * Store Payment Options (which owns the other half of the conflict)
     * cannot disagree about what counts as broken.
     *
     * Reads ``accessMode`` rather than ``campaign.access_mode`` directly, so
     * the warning tracks the radio the owner is actually clicking -- the
     * computed resolves the legacy boolean flags for rows saved before the
     * access-mode redesign, and an un-migrated store must not read as
     * "open" here when the radio shows it gated.
     */
    hasPayrollAccessConflict() {
      return isUnsellablePaymentConfiguration({
        payment_method: this.campaign?.payment_method,
        access_mode: this.accessMode,
      });
    },

    /**
     * Single source of truth for "who can shop here". Reads
     * ``campaign.access_mode`` when present, otherwise derives the
     * mode from the legacy boolean flags (so a campaign saved before
     * the redesign still picks the right radio on first render).
     *
     * The setter writes ``access_mode`` AND mirrors into the legacy
     * booleans so storefront read paths that still consult
     * ``storefront_password`` / ``is_restrict_email_domain`` stay
     * consistent during the gradual cutover.
     */
    /**
     * The mode the store HOLDS: what was last saved (or is being saved).
     * ``campaign`` carries only modes that were sent; a choice that cannot
     * be saved yet stays in ``draftAccessMode`` below.
     */
    savedMode() {
      return modeOf(this.campaign);
    },

    /**
     * The mode on screen: the owner's unsaved choice when there is one,
     * otherwise the saved mode. Choosing a mode does NOT write the store;
     * ``updateCampaign`` commits it on the next autosave only if it can be
     * saved (Bug Intake #343, #344, #346). So the rail and Review & Launch,
     * which read the store, never show a mode that is not saved.
     */
    accessMode: {
      get() {
        return this.draftAccessMode || this.savedMode;
      },
      set(value) {
        if (!VALID_ACCESS_MODES.includes(value)) value = "open";
        this.draftAccessMode = value === this.savedMode ? null : value;
        this.isStorefrontPassword = value === "password";
      },
    },

    hasStorePassword() {
      return !!this.savedAccess?.has_store_password;
    },

    /** The saved password can be offered for reveal (#496). */
    savedPasswordRevealable() {
      return (
        this.hasStorePassword &&
        !!this.savedAccess?.store_password_revealable &&
        !this.revealRefused
      );
    },

    notRevealableMessage() {
      return REVEAL_MESSAGES.store_password_not_revealable;
    },

    storageUnavailableMessage() {
      return STORAGE_UNAVAILABLE_MESSAGE;
    },

    /** Saved: Net is on and its credential is the store password (P1-1). */
    netUsesStorePassword() {
      return (
        !!this.savedAccess?.net_checkout_enabled &&
        this.savedAccess?.net_credential_mode === "store_password"
      );
    },

    passwordStatusDetail() {
      return this.hasStorePassword
        ? "Shoppers enter it to sign in. To replace it, type a new one below."
        : "Type one below. Until it is saved, shoppers cannot sign in with a password.";
    },

    passwordLabel() {
      return this.hasStorePassword ? "New store password" : this.passwordField;
    },

    passwordTooShortHint() {
      return `${PASSWORD_TOO_SHORT_MESSAGE}.`;
    },

    passwordPlaceholder() {
      return this.hasStorePassword
        ? "Type a new password to replace the current one"
        : "";
    },

    /** A password the owner typed this session, or null. */
    typedPassword() {
      if (!this.passwordInput) return null;
      const value = typeof this.password === "string" ? this.password : "";
      // The legacy placeholder is refused by the server as a password; it
      // is never put in the field any more, but is still never sent.
      if (!value.trim() || value === SAVED_PASSWORD_PLACEHOLDER) return null;
      return value;
    },

    /**
     * The typed password is under the minimum, so it is not sent yet (#443).
     * Autosave fires on every keystroke; sending each prefix made the server
     * refuse it and the refusal revert the mode, so a first password could
     * not be typed. The server keeps enforcing the minimum (it is the
     * control); this only holds the half-typed value back.
     */
    passwordTooShort() {
      const value = this.typedPassword;
      return !!value && value.trim().length < MIN_STORE_PASSWORD_LENGTH;
    },

    /** A typed password that is long enough to save, or null. */
    sendablePassword() {
      return this.passwordTooShort ? null : this.typedPassword;
    },

    /**
     * The domain list on screen while Email-domain restricted is chosen,
     * else the saved list (the child is not mounted in other modes, and
     * leaving the mode keeps the saved domains for later).
     */
    domainsOnScreen() {
      if (this.accessMode === "email_domain" && this.screenDomains) {
        return this.screenDomains;
      }
      return Array.isArray(this.campaign?.email_domains)
        ? this.campaign.email_domains
        : [];
    },

    /**
     * Why the choice on screen cannot be saved YET -- something the owner
     * completes in this section (add a domain, type a password). Asked of
     * the shared rule table, so it agrees with the server. Payroll + open is
     * not in this list: it is not completed here, so it is sent and the
     * server's refusal reverts it with the reason (Jake, 2026-10-15).
     */
    pendingAccessProblem() {
      const problems = accessConfigProblems({
        access_mode: this.accessMode,
        payment_method: this.campaign?.payment_method,
        has_store_password: this.hasStorePassword || !!this.sendablePassword,
        email_domains: this.domainsOnScreen,
      });
      return (
        problems.find(
          (p) =>
            p === PROBLEMS.PASSWORD_NOT_SET ||
            p === PROBLEMS.EMAIL_DOMAINS_EMPTY
        ) || null
      );
    },

    pendingAccessMessage() {
      const saved = MODE_LABELS[this.savedMode] || this.savedMode;
      if (this.pendingAccessProblem === PROBLEMS.EMAIL_DOMAINS_EMPTY) {
        return `Not saved yet: add at least one approved email domain. Until you do, this store stays "${saved}".`;
      }
      if (
        this.pendingAccessProblem === PROBLEMS.PASSWORD_NOT_SET &&
        this.passwordTooShort
      ) {
        return `Not saved yet: ${PASSWORD_TOO_SHORT_MESSAGE.toLowerCase()}. Until it is, this store stays "${saved}".`;
      }
      if (this.pendingAccessProblem === PROBLEMS.PASSWORD_NOT_SET) {
        return `Not saved yet: type a store password. Until you do, this store stays "${saved}".`;
      }
      return "";
    },

    /**
     * URL of the uploaded sign-in logo, or null when the store has none
     * (unset, or the legacy placeholder sentinel). Read straight from
     * ``campaign.storefront_login_image`` so the column is the single
     * source of truth -- the save path never re-writes it from a copy.
     */
    signInLogoUrl() {
      const key = this.campaign?.storefront_login_image;
      if (!key || key === SIGN_IN_NO_LOGO_SENTINEL) return null;
      return this.getImage(key);
    },

    signInPreviewSrc() {
      // No logo: the same default the storefront shows (#395).
      return this.signInLogoUrl || DEFAULT_SIGN_IN_LOGO_SRC;
    },

    emailRules() {
      return !this.emailError || this.emailError;
    },
    usernameRules() {
      return !this.usernameError || this.usernameError;
    },
    customLinkRules() {
      const rules = [];
      rules.push(
        (v) => !v || (v && !v.includes(" ")) || "Url cannot contain spaces"
      );
      rules.push(
        (v) => !v || (v && !v.includes("/")) || "Url cannot contain backslash"
      );
      rules.push(
        (v) => !v || (v && !v.includes("'")) || "Url cannot contain apostrophe"
      );
      rules.push(this.validateNotEmpty);
      return rules;
    },
    passwordRules() {
      const rules = [];
      // Required only when the store has no stored password: a stored one
      // is kept unless the owner types a new one (#342).
      if (this.accessMode === "password" && !this.hasStorePassword) {
        rules.push(this.validateNotEmpty);
      }
      return rules;
    },
    campaignSwitchDisabled() {
      let disabled = false;

      if (
        this.$store.state.account &&
        this.$store.state.account.role.name == "Client Admin"
      ) {
        disabled = true;
      }

      return disabled;
    },
    loginButtonTextColor() {
      // if color is too light, set text color to black
      if (
        this.campaign.brand_color &&
        this.setBlackIfTooLight(this.campaign.brand_color) !=
          this.campaign.brand_color &&
        this.campaign.brand_color != "#FFFFFF"
      ) {
        return "black";
      } else {
        return "white";
      }
    },

    loginButtonColor() {
      if (
        !this.campaign.brand_color ||
        this.campaign.brand_color == "#FFFFFF"
      ) {
        return "#000000";
      } else {
        return this.campaign.brand_color;
      }
    },
  },

  data() {
    return {
      valid: null,
      account: Account.generateEmpty(),
      showPassword: false,
      password: "",
      passwordInput: false,
      userExist: false,
      customLink: "",
      invalidCharacters: [" ", "/"],
      customUrlValid: null,
      hasInitialCredentials: false,
      isStorefrontPassword: false,
      imageUploading: false,
      // The owner's mode choice while it cannot be saved yet (or until the
      // next autosave commits it). null = showing the saved mode.
      draftAccessMode: null,
      // The approved domains on screen (StoreAccessControl reports them).
      screenDomains: null,
      // Why the typed Custom link was not saved (e.g. already in use).
      customLinkError: "",
      // The server's message for a refused store-login (password) save;
      // null after a good one.
      saveError: null,
      // The SAVED password while the owner has it revealed (#496); null
      // when hidden. Memory only, cleared whenever the saved state changes.
      revealedPassword: null,
      revealingPassword: false,
      // The server said the saved copy cannot be revealed (409).
      revealRefused: false,
      // Why a reveal failed, in the editor's own words (never server text).
      savedPasswordError: null,
    };
  },

  async mounted() {
    let qrcodeScript = document.createElement("script");
    qrcodeScript.setAttribute(
      "src",
      "https://cdn.jsdelivr.net/npm/davidshimjs-qrcodejs@0.0.2/qrcode.min.js"
    );
    document.head.appendChild(qrcodeScript);
    // Tracks whether the USER has edited the Custom Link field this session.
    // The ``campaign.slug`` watcher must never overwrite a value the user
    // typed with a late-arriving server slug, so it consults this flag.
    this._customLinkTouched = false;
  },

  watch: {
    // A new saved state (after every save, or a reload) may describe a
    // different password: never keep showing the old one (#496).
    savedAccess() {
      this.revealedPassword = null;
      this.revealRefused = false;
      this.savedPasswordError = null;
    },
    campaign: {
      immediate: true,
      handler(newVal) {
        if (this.editMode && !this._hasInitialized && newVal?._jv?.id) {
          this._hasInitialized = true;
          this.fillPreliminaryData();
        }
      },
    },

    // The Custom Link value is derived from ``campaign.slug``, but the
    // one-shot ``fillPreliminaryData`` above latches on the FIRST campaign
    // object that carries ``_jv.id`` -- which may arrive WITHOUT a slug (the
    // slug can merge in from a later response). Once ``_hasInitialized`` is
    // set, fillPreliminaryData never re-runs, so a late slug would leave the
    // field empty. Watch slug directly and back-fill customLink whenever the
    // slug appears -- but ONLY while the field is still empty and the user
    // has not typed into it, so a late slug can never clobber a user edit.
    "campaign.slug": {
      handler(newSlug) {
        if (!this.editMode) return;
        if (this._customLinkTouched) return;
        if (this.customLink) return;
        if (newSlug) {
          this.customLink = String(newSlug).toLowerCase();
        }
      },
    },

    valid: function(newVal, oldVal) {
      if (!this._hasInitialized) return;
      this.updateTabInvalid(!(newVal && this.customUrlValid));
    },
    customUrlValid: function(newVal, oldVal) {
      if (!this._hasInitialized) return;
      this.updateTabInvalid(!(newVal && this.customUrlValid));
    },
    // Editing the Custom link never changes who can shop (Jake,
    // 2026-10-15, Bug Intake #345): it used to switch a password store to
    // Open whenever the field was emptied, and autosave it. An empty or
    // invalid link only flags the section until it is fixed, and is not
    // saved (updateCampaign keeps the saved link).
    customLink: function() {
      this.customLinkError = "";
      this.$store.commit("setShopperURLChanged", true);
    },
  },

  methods: {
    /**
     * Show or hide the SAVED store password (#496). Showing asks the server
     * each time (the reveal is audited); hiding drops it from memory.
     */
    async toggleSavedPassword() {
      if (this.revealedPassword !== null) {
        this.revealedPassword = null;
        return;
      }
      if (this.revealingPassword) return;
      const storeId = this.savedAccess?.store_id ?? this.campaign?._jv?.id;
      if (storeId == null) return;
      this.revealingPassword = true;
      this.savedPasswordError = null;
      try {
        const resp = await this.$axios.post(
          `/stores/${storeId}/store_password/reveal/`
        );
        const value = resp?.data?.password;
        if (typeof value === "string") {
          this.revealedPassword = value;
        } else {
          this.savedPasswordError = REVEAL_FAILED_MESSAGE;
        }
      } catch (err) {
        const code = err?.response?.data?.code;
        // Status only: the response body is never logged.
        console.warn(
          "[StoreShopper] saved password reveal refused",
          err?.response?.status ?? "network"
        );
        if (code === "store_password_not_revealable") {
          this.revealRefused = true;
        } else {
          this.savedPasswordError = REVEAL_MESSAGES[code] || REVEAL_FAILED_MESSAGE;
        }
      } finally {
        this.revealingPassword = false;
      }
    },

    // Sign-in logo: the same path Branding uses for its images -- POST
    // /upload_image, set the store column in place, emit campaign-changed
    // so the parent autosave persists it through update_campaign.
    async onSignInLogoUpload(file) {
      if (!file || !this.campaign) return;
      this.imageUploading = true;
      const form = new FormData();
      form.append("file", file);
      try {
        const resp = await this.$axios.post(`/upload_image`, form);
        const uuid = resp?.data?.uuid;
        if (uuid) {
          this.campaign.storefront_login_image = uuid;
          this.$emit("campaign-changed");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to upload sign-in logo", describeError(err));
        this.$store.dispatch("raiseError", "Error uploading image!");
      } finally {
        this.imageUploading = false;
      }
    },

    async fetchStoreShopperUsername(campaignId) {
      try {
        const resp = await this.$axios.get(
          `/campaign/getShopperInfoFromCampaignId/${campaignId}`
        );
        return resp?.data?.username || null;
      } catch (err) {
        // The caller then invents a username, which creates a second
        // shopper for this store -- leave a trace when that happens.
        // eslint-disable-next-line no-console
        console.warn("Could not load the store's shopper login", describeError(err));
        return null;
      }
    },

    onSignInLogoRemove() {
      if (!this.campaign) return;
      this.campaign.storefront_login_image = null;
      this.$emit("campaign-changed");
    },

    onSignInLogoError(message) {
      this.$store.dispatch("raiseError", message);
    },

    validateTab() {
      this.$refs.form.validate();
      return !(this.valid && this.customUrlValid);
    },

    async copyUrlToClipboard() {
      const url = this.baseUrl + "/shop/" + this.customLink;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
        } else {
          // Fallback for non-HTTPS contexts (e.g. sandbox on HTTP)
          const textarea = document.createElement("textarea");
          textarea.value = url;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        this.$store.dispatch("raiseSuccess", "Link copied to clipboard");
      } catch (err) {
        this.$store.dispatch("raiseError", "Failed to copy link");
      }
    },

    allowEmptyEditMode(value) {
      if (!this.editMode && value === "") {
        return "Required";
      }
      return true;
    },

    // The user typed into the Custom Link field. Mark it touched so the
    // ``campaign.slug`` watcher stops back-filling it, then propagate the
    // change up (the pre-redesign ``@update:modelValue`` behavior).
    onCustomLinkInput() {
      this._customLinkTouched = true;
      this.$emit("campaign-changed");
    },

    fillPreliminaryData() {
      // Matched by role SLUG (or the id as a string): after a save the
      // account's role_id is the role's string ``_jv.id``, and a strict
      // compare against parseInt(...) then found no shopper on the next
      // mount (#342 path B).
      let account = (this.$props.campaign.accounts || []).filter(
        (obj) =>
          (obj.role_slug && obj.role_slug === this.targetRole.slug) ||
          String(obj.role_id) === String(this.targetRole._jv.id)
      )[0];
      if (account && account.username) {
        this.$data.account = account;
        this.hasInitialCredentials = true;
      }
      this.inputUsername();
      // slug-unify Phase 4 PR-3 (#2286): the Custom Link display reads the
      // canonical ``slug`` (the response no longer carries ``shopper_url``).
      this.customLink = this.campaign.slug
        ? this.campaign.slug.toLowerCase()
        : "";
      this.isStorefrontPassword = this.campaign.storefront_password;
    },

    // Called by the editor's save step with the server's refusal message,
    // or null when the save went through.
    showSaveError(message) {
      this.saveError = message || null;
    },

    /**
     * The editor's save step calls this once /campaign/updateShopper has
     * accepted ``sentPassword``. The typed value stops being "pending": later
     * autosaves send null (keep the stored one) instead of re-sending, and so
     * re-sealing, the same password on every unrelated edit. Skipped when the
     * owner has kept typing since (the field no longer holds what was sent).
     */
    markPasswordSaved(sentPassword) {
      if (this.password !== sentPassword) return;
      // The field keeps what was typed (no clobber of in-progress input).
      this.passwordInput = false;
    },

    changePassword() {
      this.passwordInput = true;
      this.$emit("campaign-changed");
      this.$store.commit("getShopperAccountChanged", true);
    },

    inputUsername() {
      // Matched by role SLUG, never a role-id literal: role ids differ
      // between seeded databases and production (storeAccountRoles.js).
      if (this.campaign.accounts) {
        if (this.targetRole.name === "Shopper") {
          // check if shopper name is same with adminbuyer name
          var result = this.campaign.accounts.filter((obj) => {
            return (
              obj.role_slug === ROLE_SLUGS.CLIENT_ADMIN &&
              obj.username == this.account.username
            );
          });
          if (result && result.length > 0) {
            this.userExist = true;
          } else {
            this.userExist = false;
          }
        }
        if (this.targetRole.name === "Client Admin") {
          // check if adminbuyer name is same with shopper name
          var result = this.campaign.accounts.filter((obj) => {
            return (
              obj.role_slug === ROLE_SLUGS.SHOPPER &&
              obj.username == this.account.username
            );
          });
          if (result && result.length > 0) {
            this.userExist = true;
          } else {
            this.userExist = false;
          }
        }
      }
    },

    validateUserExist(val) {
      return !val || "Username already exists";
    },

    updateTabInvalid(value) {
      this.$emit("update-tab-invalid", "shopper", value);
    },

    downloadQRCode() {
      const makeQR = (url, filename) => {
        var qrcode = new QRCode("qrcode", {
          text: url,
          width: 128,
          height: 128,
          colorDark: "#000000",
          colorLight: "#ffffff",
          correctLevel: QRCode.CorrectLevel.H,
        });
        qrcode.makeCode(url);

        setTimeout(() => {
          let qelem = document.querySelector("#qrcode img");
          let dlink = document.querySelector("#qrdl");
          let qr = qelem.getAttribute("src");
          dlink.setAttribute("href", qr);
          dlink.setAttribute("download", filename);
          dlink.click();
        }, 500);
      };
      var element = document.getElementById("qrcode");
      while (element.firstChild) {
        element.removeChild(element.firstChild);
      }
      makeQR(
        this.baseUrl + "/shop/" + this.customLink,
        "qr-code.png"
      );
    },

    /**
     * The editor saved and then REFUSED (or re-read) the store: show the
     * saved state. The parent has already put the saved slice back on
     * ``campaign``. A choice that was never sent -- still incomplete on
     * screen -- is the owner's in-progress input and is left alone.
     */
    adoptSavedAccess(saved) {
      if (this.pendingAccessProblem) return;
      this.draftAccessMode = null;
      this.$refs.accessControl?.adoptSaved(saved?.email_domains);
    },

    async updateCampaign(campaign) {
      campaign.error = null;
      let accountInvalid = false;

      // Store Login rule slice. Commit the choice on screen only when it
      // can be saved; otherwise the store keeps its saved mode and domains
      // and the section says why (pendingAccessMessage). Nothing invalid is
      // ever sent, so an incomplete choice cannot block the rest of the
      // save, and leaving the section discards it (Bug Intake #313, #343,
      // #346). The domain list is read from the section at save time, so
      // what is judged is exactly what would be sent.
      if (this.accessMode === "email_domain" && this.$refs.accessControl) {
        this.screenDomains = this.$refs.accessControl.getData().email_domains;
      }
      let mode;
      if (this.pendingAccessProblem) {
        accountInvalid = true;
        mode = modeOf(campaign);
      } else {
        mode = this.accessMode;
        campaign.access_mode = mode;
        campaign.storefront_password = mode === "password";
        campaign.is_restrict_email_domain = mode === "email_domain";
        if (mode === "email_domain" && this.$refs.accessControl) {
          campaign.email_domains = this.$refs.accessControl.getData().email_domains;
        }
        this.draftAccessMode = null;
      }

      let campaign_id = campaign._jv && campaign._jv.id ? campaign._jv.id : 0;
      // Outside password mode the shopper is sent with a null password,
      // which only means "keep it" when the username IS the store's
      // shopper. A remounted section has lost that account (the save loop
      // empties campaign.accounts), so adopt the server's before inventing
      // one -- a fresh username would create a second shopper.
      if (!this.hasInitialCredentials && !this._shopperFromServer && campaign_id) {
        const existing = await this.fetchStoreShopperUsername(campaign_id);
        if (existing) {
          this.account.username = existing;
          this._shopperFromServer = true;
        }
      }
      if (!this.account.username) {
        this.account.username = "shopper_" + campaign_id + "_" + Date.now();
      }

      // The Custom link is saved only when it is valid and free. An empty,
      // malformed or taken link keeps the SAVED link (the server would
      // otherwise reshape it or silently save ``<link>-2``, #347) and flags
      // the section -- it never changes the access mode (#345).
      let shopper_url = campaign.slug || "";
      const typedLink = (this.customLink || "").split(" ").join("").toLowerCase();
      if (!typedLink || this.customUrlValid === false) {
        accountInvalid = true;
      } else if (this.$store.getters.getShopperUrlChanged) {
        let result = await this.$axios.get(
          `/campaign/checkExistingShopperURLByDistributor/${campaign_id}/${campaign.distributor_id}/${typedLink}`
        );
        if (result.data.count > 0) {
          this.customLinkError = "This link is already used by another store.";
          accountInvalid = true;
        } else {
          shopper_url = typedLink;
        }
      } else {
        shopper_url = typedLink;
      }

      // Outside password mode, and in password mode with nothing typed, the
      // shopper is sent with a null password: "keep the stored one". Only a
      // password the owner typed is ever sent.
      const password =
        mode === "password" && this.sendablePassword ? this.sendablePassword : null;

      // validate lack of conflict between the usernames of the two accounts in the store
      if (campaign.accounts) {
        let otherAccount = campaign.accounts.filter(
          (obj) => String(obj.role_id) === String(this.otherRole._jv.id)
        )[0];

        if (otherAccount && otherAccount.username === this.account.username) {
          accountInvalid = true;
          campaign.error = "Username already exists";
        }

        // if there is an existing model in there, update it rather than create a new one
        let account = campaign.accounts.filter(
          (obj) => String(obj.role_id) === String(this.targetRole._jv.id)
        )[0];
        if (account) {
          account.distributor_id = this.$props.campaign.distributor_id;
          account.username = this.account.username;
          account.password = password;
          account.role = this.targetRole;
          account.role_id = this.targetRole._jv.id;
          account.role_slug = this.targetRole.slug;
          // slug-unify Phase 4 (#2286): the WRITE stays under the legacy
          // ``shopper_url`` BODY KEY -- the whole campaign object is POSTed
          // to update_campaign, which routes this key to ``slug`` via
          // _apply_custom_link_to_slug (normalize + uniquify). This is the
          // request-payload key, NOT the dropped column. Also mirror the
          // sanitized value into the local ``slug`` so the in-memory object
          // matches what the Custom Link display reads back (the response
          // carries ``slug``, not ``shopper_url``).
          campaign.shopper_url = shopper_url;
          campaign.slug = shopper_url;
          this.updateTabInvalid(accountInvalid);
          return campaign;
        }
      }

      // if we get to this point, there are either no accounts associated with this campaign or
      // there was no shopper in the list of associated accounts. either way, we are going to
      // have to create a shopper model.
      this.account.distributor_id = this.$props.campaign.distributor_id;
      this.account.role = this.targetRole;
      this.account.role_id = this.targetRole._jv.id;
      // The parent's save loop classifies accounts by ``role_slug`` alone
      // (classifyStoreAccount). Without it this new shopper fell through to
      // a generic POST /accounts -- an orphan account never linked to the
      // store, then a duplicate-username 500 on every later autosave --
      // instead of /campaign/updateShopper, which creates AND links it.
      this.account.role_slug = this.targetRole.slug;
      // updateShopper creates a missing shopper with no password and
      // leaves an existing one's alone when this is null.
      this.account.password = password;

      // ultimately, the method that saves this campaign to the database will have to sort out
      // whether to update an existing account model or create a new one.
      if (campaign.accounts) {
        campaign.accounts.push(this.account);
      } else {
        campaign.accounts = [this.account];
      }

      // slug-unify Phase 4 (#2286): WRITE stays under the legacy
      // ``shopper_url`` BODY KEY (the whole object is POSTed to
      // update_campaign, which routes this key to ``slug``); mirror into
      // the local ``slug`` so the display re-reads consistently after
      // save. This is the request-payload key, NOT the dropped column.
      campaign.shopper_url = shopper_url;
      campaign.slug = shopper_url;
      this.updateTabInvalid(accountInvalid);
      return campaign;
    },
  },
};
</script>

<style scoped>
.custom-URL-Copy {
  position: relative !important;
  right: 50px !important;
  bottom: 70px !important;
}
.copy-url-button,
.download-qr-button {
  top: 16px;
  right: 16px;
  z-index: 3;
}
.disable-layer {
  width: 100%;
  position: absolute;
  z-index: 1;
  opacity: 0.6;
  background: white;
  height: -webkit-fill-available;
}

.company-logo {
  object-fit: cover !important;
  width: 350px;
  height: 350px;
  border-radius: 30px;
}
/* The default wordmark is wide: show all of it, as the storefront does. */
.company-logo--fit {
  object-fit: contain !important;
  padding: 24px;
}

.password-login-card {
  border-radius: 50px !important;
  width: 400px;
}
.btn-login {
  height: 48px !important;
}
.access-mode-coming-soon {
  font-size: 12px;
  font-style: italic;
  color: var(--tf-fg-4, #6b7280);
}

/* Each access mode is a radio + a one-line helper that explains who the
   mode is for, aligned to the design's per-option `sub` copy. */
.access-mode-option {
  margin-bottom: 12px;
}
.access-mode-option:last-child {
  margin-bottom: 0;
}
.access-mode-help {
  /* Indent to sit under the radio label, not the control glyph. */
  margin-left: 32px;
  margin-top: -2px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--tf-fg-4, #757575);
  max-width: 560px;
}
</style>
